import {COLOR_PRIMARY, COLOR_PRIMARY_HIGHLIGHT, getComputedColor, MARKER_COLOR_HIGHLIGHT_PROPS, MARKER_COLOR_PROPS} from "./colors";
import {customElement, property, query} from "lit/decorators.js";
import {UnLitElement} from "../../common/elements";
import {GoogleMapsFactory} from "./googleMapsFactory";
import {resolve} from "../../container";
import {BOOTSTRAP} from "../../common/resolutionConstants";
import {Resolution} from "../../common/resolution";
import {pinIcon} from "./pinIcon";
import {EventBus} from "../../common/eventBus";

export interface EopGoogleMapsVisual {
    renderOnMap: (map: google.maps.Map) => void;
    center: () => MapLatLng;
}

export interface EopGoogleMapsInteractiveMarker extends EopGoogleMapsVisual {
    markerElement: () => google.maps.marker.AdvancedMarkerElement
    closeOverlay: () => void;
}

export const GOOGLE_MAPS_OPEN_POI_OVERLAY = "goole-maps-open-poi-overlay";

export type MapLatLng = {
    lat: number;
    lng: number;
};

export class MapPath {
    public constructor(private coordinates: MapLatLng[]) {
    }

    public center(): MapLatLng {
        const mostNorth = Math.max(...this.coordinates.map(c => c.lat));
        const mostSouth = Math.min(...this.coordinates.map(c => c.lat));
        const mostEast = Math.max(...this.coordinates.map(c => c.lng));
        const mostWest = Math.min(...this.coordinates.map(c => c.lng));

        return {lat: (mostNorth + mostSouth) / 2, lng: (mostEast + mostWest) / 2};
    }

    public asGoogleMapsLatLng(googleMapsFactory: GoogleMapsFactory): google.maps.LatLng[] {
        return this.coordinates.map(coord => googleMapsFactory.createLatLng(coord.lat, coord.lng));
    }
}

function parseMapPathFromString(coordinates: string): MapPath {
    return new MapPath(coordinates.trim().split(/\s+/).map(c => {
        const [lng, lat, _alt] = c.split(",");
        return {lat: Number.parseFloat(lat), lng: Number.parseFloat(lng)};
    }));
}

@customElement("eop-google-maps-poi")
export class EopGoogleMapsPoi extends UnLitElement implements EopGoogleMapsInteractiveMarker {
    @property({attribute: "visual-data-title"})
    public title: string;
    @property({attribute: "color"})
    private color: string;
    @property({attribute: "lat", type: Number})
    private lat: number;
    @property({attribute: "lng", type: Number})
    private lng: number;

    @query(".close-button")
    private closeButton: HTMLElement;

    private marker: google.maps.marker.AdvancedMarkerElement;
    private iconElement: HTMLElement;

    private position: MapLatLng;

    public constructor(
        private googleMapsFactory: GoogleMapsFactory = resolve(GoogleMapsFactory),
        private resolution: Resolution = resolve(Resolution),
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.closeButton.addEventListener("click", () => this.closeOverlay());
        this.position = {lat: this.lat, lng: this.lng};
    }

    public renderOnMap(map: google.maps.Map): void {
        this.createMarkerIcon();
        this.marker = this.googleMapsFactory.createMarker({
            map: map,
            position: this.googleMapsFactory.createLatLng(this.position.lat, this.position.lng),
            title: this.title,
            content: this.iconElement,
            gmpClickable: true
        });

        this.marker.addEventListener("gmp-click", () => this.toggleOpenOverlay());
    }

    public markerElement(): google.maps.marker.AdvancedMarkerElement {
        return this.marker;
    }

    public center(): MapLatLng {
        return this.position;
    }

    public isOverlayOpen(): boolean {
        return this.classList.contains("open");
    }

    public toggleOpenOverlay(): void {
        this.isOverlayOpen() ? this.closeOverlay() : this.openOverlay();
    }

    public openOverlay(): void {
        this.eventBus.dispatchEvent(GOOGLE_MAPS_OPEN_POI_OVERLAY, {});
        this.classList.add("open");
        this.toggleIconHighlight(true);
        this.toggleMapOptions(false);
    }

    public closeOverlay(): void {
        this.classList.remove("open");
        this.toggleIconHighlight(false);
        this.toggleMapOptions(true);
    }

    private createMarkerIcon(): void {
        this.iconElement = pinIcon({width: 30, height: 35, scale: 1.5});
        this.toggleIconHighlight(false);
    }

    private toggleIconHighlight(highlight: boolean): void {
        const colorVar = highlight
            ? (MARKER_COLOR_HIGHLIGHT_PROPS[this.color] ?? COLOR_PRIMARY_HIGHLIGHT)
            : (MARKER_COLOR_PROPS[this.color] ?? COLOR_PRIMARY);
        this.iconElement.style.color = getComputedColor(this, colorVar);
    }

    private toggleMapOptions(switchedOn: boolean): void {
        this.marker.map?.setOptions({mapTypeControl: switchedOn});
        if (switchedOn || this.resolution.upTo(BOOTSTRAP.XS)) {
            this.marker.map?.setOptions({zoomControl: switchedOn, fullscreenControl: switchedOn, streetViewControl: switchedOn});
        }
    }
}

@customElement("eop-google-maps-polyline")
export class EopGoogleMapsPolyline extends UnLitElement implements EopGoogleMapsVisual {
    @property({attribute: "color"})
    private color: string;
    @property({attribute: "path"})
    private pathAttribute: string;

    private path: MapPath;

    public constructor(
        private googleMapsFactory: GoogleMapsFactory = resolve(GoogleMapsFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.path = parseMapPathFromString(this.pathAttribute);
    }

    public renderOnMap(map: google.maps.Map): google.maps.Polyline {
        return this.googleMapsFactory.createPolyline(map, this.path.asGoogleMapsLatLng(this.googleMapsFactory), this.getColor());
    }

    public center(): MapLatLng {
        return this.path.center();
    }

    private getColor(): string {
        const colorVar = MARKER_COLOR_PROPS[this.color] ?? COLOR_PRIMARY;
        return getComputedColor(this, colorVar);
    }

    public close(): void {
        // No-op
    }
}

@customElement("eop-google-maps-polygon")
export class EopGoogleMapsPolygon extends UnLitElement implements EopGoogleMapsVisual {
    @property({attribute: "color"})
    private color: string;
    @property({attribute: "outer-path"})
    private outerPathAttribute: string;

    private outerPath: MapPath;

    public constructor(
        private googleMapsFactory: GoogleMapsFactory = resolve(GoogleMapsFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.outerPath = parseMapPathFromString(this.outerPathAttribute);
    }

    public renderOnMap(map: google.maps.Map): google.maps.Polygon {
        return this.googleMapsFactory.createPolygon(map, this.outerPath.asGoogleMapsLatLng(this.googleMapsFactory), this.getColor());
    }

    public center(): MapLatLng {
        return this.outerPath.center();
    }

    private getColor(): string {
        const colorVar = MARKER_COLOR_PROPS[this.color] ?? COLOR_PRIMARY;
        return getComputedColor(this, colorVar);
    }

    public close(): void {
        // No-op
    }
}